import type { FrameQuality } from "../types.js";

/**
 * Analyze a luma buffer to decide *why* a decode attempt might fail:
 * too dark, blown out by glare/reflection, or too blurry to resolve
 * module edges. This drives which enhancement (if any) the pipeline
 * applies next, instead of always running the full (costly) chain.
 */
export function analyzeFrame(luma: Uint8ClampedArray, width: number, height: number): FrameQuality {
  const n = luma.length;
  let sum = 0;
  let glare = 0;
  let shadow = 0;
  // Sample every 2nd pixel; ROI buffers are already small (viewfinder-sized),
  // so this stays cheap while still being representative.
  const stride = 2;
  let sampled = 0;
  for (let i = 0; i < n; i += stride) {
    const v = luma[i];
    sum += v;
    if (v >= 248) glare++;
    if (v <= 18) shadow++;
    sampled++;
  }
  const meanLuma = sum / sampled;
  const glareFraction = glare / sampled;
  const shadowFraction = shadow / sampled;
  const focusScore = laplacianVariance(luma, width, height);

  // A large near-white fraction is ambiguous on its own: real specular
  // glare is a *localized* hotspot, but a barcode's own quiet zone / a
  // plain white label background is legitimately bright over most of the
  // frame and must not be treated as glare (verified empirically — a
  // blurred code on a plain white background was pegged at ~70% "glare"
  // pixels despite no reflection at all in the shot). Cap it: past a point
  // it's read as "this frame is just bright", not "there's a hotspot".
  const isLocalizedGlare = glareFraction > 0.06 && glareFraction <= 0.4;

  let condition: FrameQuality["condition"] = "good";
  if (meanLuma < 60) condition = "low-light";
  else if (isLocalizedGlare) condition = "glare";
  else if (focusScore < 12) condition = "blurry";
  else if (glareFraction > 0.015 && meanLuma < 110) condition = "mixed";

  return { meanLuma, glareFraction, shadowFraction, focusScore, condition };
}

/**
 * Cheap sharpness estimate: variance of a 3x3 Laplacian response.
 * Low variance = flat/blurry image, high variance = crisp edges
 * (which barcode module transitions produce plenty of).
 * Operates on a horizontal/vertical stride to keep this O(n) and fast
 * enough to run every frame on modest hardware.
 */
export function laplacianVariance(luma: Uint8ClampedArray, width: number, height: number): number {
  if (width < 3 || height < 3) return 0;
  const stepX = Math.max(1, Math.floor(width / 160));
  const stepY = Math.max(1, Math.floor(height / 160));
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += stepY) {
    const row = y * width;
    const rowUp = row - width;
    const rowDown = row + width;
    for (let x = 1; x < width - 1; x += stepX) {
      const lap =
        4 * luma[row + x] - luma[rowUp + x] - luma[rowDown + x] - luma[row + x - 1] - luma[row + x + 1];
      sum += lap;
      sumSq += lap * lap;
      count++;
    }
  }
  if (count === 0) return 0;
  const mean = sum / count;
  return sumSq / count - mean * mean;
}
